import React, { useState, useEffect } from 'react';
import {
  Table,
  Pane,
  Dialog,
  Text,
  Heading,
  Paragraph,
  majorScale,
  IconButton,
} from 'evergreen-ui';
import {
  PDFDownloadLink,
  Page,
  View,
  Document,
  Image,
  StyleSheet,
  Text as PDFText,
} from '@react-pdf/renderer';
import QRCode from 'qrcode';
import { FaFileDownload, FaEye } from 'react-icons/fa'; // Import icons from react-icons

// Import images from the assets folder
import logo from '../../assets/Chasylmlogo.png';
import watermark from '../../assets/Chasylmlogo.png';

// PDF Document Component
const ReceiptDocument = ({ ticket }) => {
  const [qrCodeImage, setQrCodeImage] = useState(null);

  useEffect(() => {
    // Combine ticket information for QR code
    const qrValue = `
      Tour ID: ${ticket.tour_id},
      First Name: ${ticket.firstName},
      Last Name: ${ticket.lastName},
      Email: ${ticket.email},
      Phone: ${ticket.phone},
      Destination Name: ${ticket.destinationName},
      Amount: ${ticket.amount},
      Currency: ${ticket.currency},
      Travel Date: ${ticket.travel_date},
      Number of Adults: ${ticket.adult},
      Number of Children: ${ticket.child},
      Remaining Amount: ${ticket.remainingAmount},
      Payment Type: ${ticket.paymentType},
      Transaction Date: ${ticket.created_date},
      Order Tracking ID: ${ticket.order_tracking_id},
      Merchant Reference: ${ticket.merchant_reference},
      Payment Method: ${ticket.payment_method},
      Confirmation Code: ${ticket.confirmation_code},
      Payment Status Description: ${ticket.payment_status_description},
      Description: ${ticket.description},
      Message: ${ticket.message},
      Payment Account: ${ticket.payment_account},
      Payment Status Code: ${ticket.payment_status_code},
      Status: ${ticket.status}
    `;

    // Generate QR code as base64 image
    QRCode.toDataURL(qrValue, { width: 100, margin: 2 }, (err, url) => {
      if (!err) {
        setQrCodeImage(url);
      }
    });
  }, [ticket]);

  const styles = StyleSheet.create({
    page: {
      padding: 30,
      position: 'relative',
      backgroundColor: '#F2EED7',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 20,
    },
    contactInfo: {
      top: 20,
      fontSize: 10,
      textAlign: 'right',
    },
    detailsText: {
      fontSize: 12,
    },
    logo: {
      width: 100,
      height: 80,
      marginBottom: 10,
    },
    watermark: {
      position: 'absolute',
      width: '90%',
      height: '60%',
      opacity: 0.1,
      top: 100,
      left: 0,
    },
    table: {
      display: 'table',
      width: '100%',
      borderStyle: 'solid',
      borderWidth: 1,
      margin: '10px 0',
    },
    tableRow: {
      margin: 'auto',
      flexDirection: 'row',
    },
    tableCol: {
      width: '16.66%',
      borderStyle: 'solid',
      borderWidth: 1,
      padding: 8,
    },
    tableCell: {
      margin: 'auto',
      fontSize: 10,
    },
    title: {
      fontSize: 16,
      textAlign: 'center',
      marginBottom: 10,
    },
    section: {
      marginBottom: 10,
    },
    qrCodeContainer: {
      textAlign: 'start',
      marginTop: 20,
      marginBottom: 20,
    },
    qrCode: {
      width: 100,
      height: 100,
      marginTop: 10,
    },
  });

  return (
    <Document>
      <Page style={styles.page}>
        <View style={styles.header}>
          <Image src={logo} style={styles.logo} />
          <View style={styles.contactInfo}>
            <PDFText>info@chasylmtours.com</PDFText>
            <PDFText>Tel: +254720727776</PDFText>
            <PDFText>Nairobi, Kenya</PDFText>
          </View>
        </View>

        <Image src={watermark} style={styles.watermark} />

        <PDFText style={styles.title}>Chasylm Tours & Safaris Receipt</PDFText>

        <View style={styles.section}>
          <PDFText style={styles.detailsText}>
            Name: {ticket.firstName} {ticket.lastName}
          </PDFText>
          <PDFText style={styles.detailsText}>Phone: {ticket.phone}</PDFText>
          <PDFText style={styles.detailsText}>Email: {ticket.email}</PDFText>
          <PDFText style={styles.detailsText}>Destination: {ticket.destinationName}</PDFText>
          <PDFText style={styles.detailsText}>Travel Date: {ticket.travel_date}</PDFText>
        </View>

        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>Payment Method</PDFText>
            </View>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>Amount (USD)</PDFText>
            </View>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>Merchant Reference</PDFText>
            </View>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>Payment Date</PDFText>
            </View>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>No. of Adult(s)</PDFText>
            </View>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>No. of Child(ren)</PDFText>
            </View>
          </View>

          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>{ticket.payment_method}</PDFText>
            </View>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>{ticket.amount}</PDFText>
            </View>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>{ticket.merchant_reference}</PDFText>
            </View>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>{ticket.created_date}</PDFText>
            </View>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>{ticket.adult}</PDFText>
            </View>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>{ticket.child}</PDFText>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <PDFText style={styles.detailsText}>Tracking ID: {ticket.order_tracking_id}</PDFText>
          <PDFText style={styles.detailsText}>Payment Account: {ticket.payment_account}</PDFText>
          <PDFText style={styles.detailsText}>Confirmation Code: {ticket.confirmation_code}</PDFText>
          <PDFText style={styles.detailsText}>Balance: {ticket.remainingAmount}</PDFText>
        </View>

        <View style={styles.section}>
          <PDFText style={styles.detailsText}>Message: {ticket.message}</PDFText>
        </View>

        {qrCodeImage && (
          <View style={styles.qrCodeContainer}>
            <PDFText style={styles.detailsText}>Scan for more details</PDFText>
            <Image src={qrCodeImage} style={styles.qrCode} />
          </View>
        )}

        <PDFText style={styles.detailsText}>Thank you for booking with us!</PDFText>
      </Page>
    </Document>
  );
};

// Main BookedTickets Component
const BookedTickets = ({ userEmail, userPhone }) => {
  const [bookedTickets, setBookedTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isDialogShown, setIsDialogShown] = useState(false);

  // Fetch booked tickets
  const fetchBookedTickets = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/pesapal/receipts`);
      if (!response.ok) {
        throw new Error(`Failed to fetch booked tickets: ${response.status}`);
      }
      const ticketsData = await response.json();
      setBookedTickets(ticketsData);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookedTickets();
  }, []);

  const filteredTickets = bookedTickets.filter(
    (ticket) => ticket.email === userEmail || ticket.phone === userPhone
  );

  const handleShowDetails = (ticket) => {
    setSelectedTicket(ticket);
    setIsDialogShown(true);
  };

  if (loading) {
    return (
      <Pane display="flex" justifyContent="center" padding={majorScale(3)}>
        <Text>Loading booked tickets...</Text>
      </Pane>
    );
  }

  if (error) {
    return (
      <Pane display="flex" justifyContent="center" padding={majorScale(3)}>
        <Text color="danger">Error: {error}</Text>
      </Pane>
    );
  }

  return (
    <Pane padding={majorScale(3)} background="tint1">
      <Heading size={800} marginBottom={majorScale(3)}>
        Booked Tickets
      </Heading>

      {filteredTickets.length === 0 ? (
        <Paragraph>No tickets booked yet.</Paragraph>
      ) : (
        <Table>
          <Table.Head>
            <Table.TextHeaderCell>Name</Table.TextHeaderCell>
            <Table.TextHeaderCell>Email</Table.TextHeaderCell>
            <Table.TextHeaderCell>Destination</Table.TextHeaderCell>
            <Table.TextHeaderCell>Amount</Table.TextHeaderCell>
            <Table.TextHeaderCell>Status</Table.TextHeaderCell>
            <Table.TextHeaderCell>Date</Table.TextHeaderCell>
            <Table.TextHeaderCell>Actions</Table.TextHeaderCell>
          </Table.Head>
          <Table.Body>
            {filteredTickets.map((ticket) => (
              <Table.Row key={ticket.id}>
                <Table.TextCell>
                  {`${ticket.firstName} ${ticket.lastName}`}
                </Table.TextCell>
                <Table.TextCell>{ticket.email}</Table.TextCell>
                <Table.TextCell>{ticket.destinationName}</Table.TextCell>
                <Table.TextCell>
                  {ticket.amount} {ticket.currency}
                </Table.TextCell>
                <Table.TextCell>{ticket.status}</Table.TextCell>
                <Table.TextCell>{ticket.created_date}</Table.TextCell>
                <Table.Cell padding={majorScale(1)}>
                  <Pane display="flex" gap={majorScale(1)} alignItems="center">
                    <PDFDownloadLink
                      document={<ReceiptDocument ticket={ticket} />}
                      fileName={`receipt_${ticket.id}.pdf`}
                    >
                      {({ loading }) => (
                        <IconButton
                          icon={<FaFileDownload />}
                          disabled={loading}
                          title="Print Receipt"
                        />
                      )}
                    </PDFDownloadLink>
                    <IconButton
                      icon={<FaEye />}
                      appearance="primary"
                      intent="success"
                      onClick={() => handleShowDetails(ticket)}
                      title="View Details"
                    />
                  </Pane>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <Dialog
        isShown={isDialogShown}
        title="Ticket Details"
        onCloseComplete={() => setIsDialogShown(false)}
        confirmLabel="Close"
        hasCancel={false}
        width={600}
      >
        {selectedTicket && (
          <Pane padding={majorScale(2)}>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Tour ID:</strong> {selectedTicket.tour_id}
            </Paragraph>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Phone:</strong> {selectedTicket.phone}
            </Paragraph>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Travel Date:</strong> {selectedTicket.travel_date}
            </Paragraph>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Number of Adults:</strong> {selectedTicket.adult}
            </Paragraph>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Number of Children:</strong> {selectedTicket.child}
            </Paragraph>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Remaining Amount:</strong> {selectedTicket.remainingAmount}
            </Paragraph>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Payment Type:</strong> {selectedTicket.paymentType}
            </Paragraph>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Order Tracking ID:</strong> {selectedTicket.order_tracking_id}
            </Paragraph>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Merchant Reference:</strong> {selectedTicket.merchant_reference}
            </Paragraph>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Payment Method:</strong> {selectedTicket.payment_method}
            </Paragraph>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Confirmation Code:</strong> {selectedTicket.confirmation_code}
            </Paragraph>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Payment Status:</strong> {selectedTicket.payment_status_description}
            </Paragraph>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Payment Account:</strong> {selectedTicket.payment_account}
            </Paragraph>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Description:</strong> {selectedTicket.description}
            </Paragraph>
            <Paragraph marginBottom={majorScale(1)}>
              <strong>Message:</strong> {selectedTicket.message}
            </Paragraph>
          </Pane>
        )}
      </Dialog>
    </Pane>
  );
};

export default BookedTickets;